import { DataService } from './data-service';

export enum ComplaintStatus {
  Open = 0,
  InProgress = 1,
  Resolved = 2,
  Closed = 3
}

export enum ComplaintMessageSource {
  Tenant = 0,
  Admin = 1
}

export interface ComplaintMessage {
  id: string;
  body: string;
  source: ComplaintMessageSource;
  sentAt: string;
  isMine: boolean;
  isReadByTenant: boolean;
  isReadByAdmin: boolean;
  senderName: string;
}

export interface ComplaintThread {
  id: string;
  title: string;
  status: ComplaintStatus;
  createdAt: string;
  lastActivityAt: string;
  unreadByAdminCount: number;
  unreadByTenantCount: number;
  tenantId: string;
  tenantName: string;
  messages?: ComplaintMessage[];
}

export interface ComplaintThreadDetail extends ComplaintThread {
  messages: ComplaintMessage[];
}

export interface SendMessageRequest {
  complaintThreadId: string;
  body: string;
  source: ComplaintMessageSource;
  tenantId: string | null;
  userId: string;
}

export interface UpdateStatusRequest {
  status: ComplaintStatus;
}

export interface ApiResponse<T> {
  success: boolean;
  token: string | null;
  message: string | null;
  data: T;
}

export class ComplaintsService {
  private static instance: ComplaintsService;

  private constructor() {}

  public static getInstance(): ComplaintsService {
    if (!ComplaintsService.instance) {
      ComplaintsService.instance = new ComplaintsService();
    }
    return ComplaintsService.instance;
  }

  async getAllThreads(): Promise<ComplaintThread[]> {
    try {
      const response = await DataService.get('api/complaints/threads');
      const result: ApiResponse<ComplaintThread[]> = await response.json();

      if (result.success && result.data) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to fetch complaint threads');
    } catch (error) {
      console.error('Error fetching complaint threads:', error);
      throw error;
    }
  }

  async getUnreadCount(): Promise<number> {
    try {
      const threads = await this.getAllThreads();
      return threads.reduce((total, thread) => total + thread.unreadByAdminCount, 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  async getThreadDetails(threadId: string): Promise<ComplaintThreadDetail> {
    try {
      const response = await DataService.get(`api/complaints/threads/${threadId}`);
      const result: ApiResponse<ComplaintThreadDetail> = await response.json();

      if (result.success && result.data) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to fetch thread details');
    } catch (error) {
      console.error('Error fetching thread details:', error);
      throw error;
    }
  }

  async sendMessage(
    threadId: string,
    message: string,
    userId: string
  ): Promise<ComplaintThreadDetail> {
    try {
      const requestBody: SendMessageRequest = {
        complaintThreadId: threadId,
        body: message,
        source: ComplaintMessageSource.Admin,
        tenantId: null,
        userId: userId
      };

      const response = await DataService.post('api/complaints/messages/admin', requestBody);
      const result: ApiResponse<ComplaintThreadDetail> = await response.json();

      if (result.success && result.data) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to send message');
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async updateThreadStatus(
    threadId: string,
    status: ComplaintStatus
  ): Promise<boolean> {
    try {
      const requestBody: UpdateStatusRequest = {
        status: status
      };

      const response = await DataService.put(
        `api/complaints/threads/${threadId}/status`,
        requestBody
      );
      const result: ApiResponse<boolean> = await response.json();

      if (result.success) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to update thread status');
    } catch (error) {
      console.error('Error updating thread status:', error);
      throw error;
    }
  }

  getStatusLabel(status: ComplaintStatus): string {
    switch (status) {
      case ComplaintStatus.Open:
        return 'Open';
      case ComplaintStatus.InProgress:
        return 'In Progress';
      case ComplaintStatus.Resolved:
        return 'Resolved';
      case ComplaintStatus.Closed:
        return 'Closed';
      default:
        return 'Unknown';
    }
  }

  getStatusColor(status: ComplaintStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' {
    switch (status) {
      case ComplaintStatus.Open:
        return 'error';
      case ComplaintStatus.InProgress:
        return 'warning';
      case ComplaintStatus.Resolved:
        return 'success';
      case ComplaintStatus.Closed:
        return 'default';
      default:
        return 'default';
    }
  }
}

export default ComplaintsService;
